import VariantPicker from "@theme/variant-picker";
import { Component } from "@theme/component";
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

export class ProductCard extends Component {
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checkedInput?.dataset.variantId || null;
  }

  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
    requestAnimationFrame(() => {
      this.#updateVariantImages();
    });
  };

  #updateProductUrl(event) {
    // Combined listing: update from newProduct data (works with cache)
    if (event.detail.data.newProduct?.url) {
      const productUrl = event.detail.data.newProduct.url;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      if (productCardLink) productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
      if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;

      const featuredMediaUrl = event.detail.resource?.featured_media?.preview_image?.src;
      if (featuredMediaUrl && this.closest("product-card-link")) {
        this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);
      }
      return;
    }

    // Fallback: get URL from HTML (cache miss)
    if (!event.detail.data.html) return;

    const anchorElement = event.detail.data.html.querySelector("product-card a");
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector("product-card-link")
      ?.getAttribute("data-featured-media-url");

    if (featuredMediaUrl && this.closest("product-card-link")) {
      this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute("href")?.trim() === "") return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      if (productCardLink) productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
      if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;
    }
  }

  #updateVariantImages(event) {
    const mainImage = this.refs.cardGalleryLink?.querySelector(".product-card-main-image");
    if (!mainImage) return;

    const selectedSwatch = this.querySelector('input[type="radio"]:checked');
    if (!selectedSwatch) return;

    // Combined listing: check product match
    const swatchProductId = this.variantPicker?.dataset.productId;
    const currentProductId = this.dataset.productId || this.closest("product-card")?.dataset.productId;
    if (swatchProductId && currentProductId && swatchProductId !== currentProductId) {
      return;
    }

    const selectedLabel = selectedSwatch.closest("label");
    if (!selectedLabel) return;

    // Skip if swatch is hidden
    const swatchItem = selectedLabel.closest(".variant-option__swatch");
    if (swatchItem?.classList.contains("hidden")) return;

    let selectedImage = selectedLabel.querySelector(".media__image");

    // Unavailable variant: if swatch doesn't match event variant, try HTML first
    const swatchVariantId = selectedSwatch.dataset.variantId;
    const eventVariantId = event?.detail.resource?.id;
    if (eventVariantId && swatchVariantId && swatchVariantId !== eventVariantId && event?.detail.data.html) {
      if (this.#updateImageFromHTML(mainImage, event.detail.data.html)) {
        return;
      }
    }

    // Wait for image if not found yet
    if (!selectedImage) {
      const mediaId = selectedLabel.dataset.mediaId || selectedSwatch.dataset.optionMediaId;
      if (mediaId) {
        this.#waitForImageAndUpdate(mainImage, selectedLabel);
      }
      return;
    }

    // Apply image immediately if src exists
    if (selectedImage.src || selectedImage.currentSrc) {
      this.#applyImageToMain(mainImage, selectedImage);
      if (!selectedImage.complete) {
        selectedImage.addEventListener(
          "load",
          () => {
            if (selectedImage.srcset && mainImage.srcset !== selectedImage.srcset) {
              mainImage.srcset = selectedImage.srcset;
            }
          },
          { once: true }
        );
      }
    } else {
      selectedImage.addEventListener(
        "load",
        () => {
          this.#applyImageToMain(mainImage, selectedImage);
        },
        { once: true }
      );
    }
  }

  #waitForImageAndUpdate(mainImage, label) {
    const checkImage = () => {
      const img = label.querySelector(".media__image");
      if (img && (img.complete || img.naturalWidth > 0)) {
        this.#applyImageToMain(mainImage, img);
        return true;
      }
      return false;
    };

    if (checkImage()) return;

    requestAnimationFrame(() => {
      if (checkImage()) return;
      const img = label.querySelector(".media__image");
      if (img) {
        img.addEventListener("load", () => this.#applyImageToMain(mainImage, img), { once: true });
      }
    });
  }

  #updateImageFromHTML(mainImage, html) {
    const newCard = html.querySelector(`product-card[data-product-id="${this.dataset.productId}"]`);
    if (!newCard) return false;

    const newMainImage = newCard.querySelector(".product-card-main-image");
    if (!newMainImage) return false;

    let updated = false;
    if (newMainImage.srcset && mainImage.srcset !== newMainImage.srcset) {
      mainImage.srcset = newMainImage.srcset;
      updated = true;
    }
    if (newMainImage.src && mainImage.src !== newMainImage.src) {
      mainImage.src = newMainImage.src;
      updated = true;
    }
    return updated;
  }

  #applyImageToMain(mainImage, sourceImage) {
    if (!mainImage || !sourceImage) return;

    const newSrcset = sourceImage.srcset || "";
    const newSrc = sourceImage.currentSrc || sourceImage.src;

    if (newSrcset && mainImage.srcset !== newSrcset) {
      mainImage.srcset = newSrcset;
    }
    if (newSrc && mainImage.src !== newSrc) {
      mainImage.src = newSrc;
    }
  }

  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.#updateProductUrl(event);

    if (event.target !== this.variantPicker && event.detail.data.html) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Restore swatches expanded state after morph
    if (this.variantPicker instanceof SwatchesVariantPicker && event.detail.data.html) {
      const expandedKey = this.variantPicker.getStorageKey();
      if (sessionStorage.getItem(expandedKey) === "true") {
        requestAnimationFrame(() => {
          this.variantPicker.restoreExpandedState();
        });
      }
    }

    // Update images: immediate for cache, deferred for cache miss
    if (event.detail.data.isOptimistic || event.detail.data.isBackgroundSync) {
      this.#updateVariantImages(event);
    } else {
      requestAnimationFrame(() => {
        this.#updateVariantImages(event);
      });
    }
  };

  get variantPicker() {
    return this.querySelector("swatches-variant-picker");
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

class SwatchesVariantPicker extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest("product-card");

    // Clear storage on page load (not on morph) - detect by checking if component is new
    if (!this.dataset.initialized) {
      const expandedKey = this.getStorageKey();
      sessionStorage.removeItem(expandedKey);
      this.dataset.initialized = "true";
    }
  }

  updatedCallback() {
    super.updatedCallback?.();

    // Restore expanded state after component update (morph)
    const expandedKey = this.getStorageKey();
    const shouldExpand = sessionStorage.getItem(expandedKey) === "true";

    if (shouldExpand) {
      this.restoreExpandedState();
    }
  }

  getStorageKey() {
    const productId = this.dataset.productId || "unknown";
    return `swatches-expanded-${productId}`;
  }

  restoreExpandedState() {
    const hiddenSwatches = this.querySelectorAll(".variant-option__swatch.hidden");
    const showMoreButton = this.querySelector(".variant-option__swatch--show-more");

    hiddenSwatches.forEach((swatch) => swatch.classList.remove("hidden"));
    if (showMoreButton) showMoreButton.classList.add("hidden");
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes("-swatch");
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || "0");
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Unavailable variant with alternatives: load available variant instead
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      const productUrl = this.dataset.productUrl?.split("?")[0];
      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", firstAvailableVariantId);
      url.searchParams.set("section_id", "section-rendering-product-card");

      const fetchToken = this.beginVariantFetch();
      this.fetchUpdatedSection(url.href, false, false, fetchToken);
      return;
    }

    super.variantChanged(event);
  }

  showAllSwatches(event) {
    event?.preventDefault();
    const hiddenSwatches = this.querySelectorAll(".variant-option__swatch.hidden");
    if (hiddenSwatches.length === 0) return;

    hiddenSwatches.forEach((swatch) => swatch.classList.remove("hidden"));
    this.querySelector(".variant-option__swatch--show-more")?.classList.add("hidden");
    sessionStorage.setItem(this.getStorageKey(), "true");
  }
}
if (!customElements.get("swatches-variant-picker")) {
  customElements.define("swatches-variant-picker", SwatchesVariantPicker);
}

class ProductCardSizes extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#track = this.querySelector("[data-sizes-track]");
    this.#prev = this.querySelector("[data-sizes-prev]");
    this.#next = this.querySelector("[data-sizes-next]");
    if (!this.#track) return;

    this.#prev?.addEventListener("click", this.#onPrev);
    this.#next?.addEventListener("click", this.#onNext);
    this.addEventListener("click", this.#onStaticSizeClick);
    this.#track.addEventListener("scroll", this.#sync, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#sync);
    this.#resizeObserver.observe(this.#track);
    this.#sync();
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener("click", this.#onPrev);
    this.#next?.removeEventListener("click", this.#onNext);
    this.removeEventListener("click", this.#onStaticSizeClick);
    this.#track?.removeEventListener("scroll", this.#sync);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #onStaticSizeClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest(".product-card__size--static")) return;
    event.preventDefault();
    event.stopPropagation();
  };

  #scrollAmount() {
    if (!this.#track) return 0;
    const item = this.#track.querySelector(".product-card__size");
    if (!(item instanceof HTMLElement)) return this.#track.clientWidth;
    const styles = getComputedStyle(this.#track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const count = Number.parseFloat(styles.getPropertyValue("--product-card-size-visible")) || 3;
    return (item.getBoundingClientRect().width + gap) * count;
  }

  #onPrev = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#track?.scrollBy({ left: -this.#scrollAmount(), behavior: "smooth" });
  };

  #onNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#track?.scrollBy({ left: this.#scrollAmount(), behavior: "smooth" });
  };

  #sync = () => {
    if (!this.#track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    const maxScroll = scrollWidth - clientWidth;
    const hasOverflow = maxScroll > 1;
    this.classList.toggle("product-card__sizes--overflow", hasOverflow);
    if (this.#prev) this.#prev.disabled = !hasOverflow || scrollLeft <= 1;
    if (this.#next) this.#next.disabled = !hasOverflow || scrollLeft >= maxScroll - 1;
  };
}

if (!customElements.get("product-card-sizes")) {
  customElements.define("product-card-sizes", ProductCardSizes);
}

class ProductCardMediaCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {HTMLElement | null} */
  #pagination = null;
  /** @type {HTMLButtonElement[]} */
  #dots = [];
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {AbortController | null} */
  #abortController = null;

  #mediaGroup() {
    return this.closest(".product-card__media-group");
  }

  #resolvePagination() {
    return (
      this.querySelector("[data-media-pagination]") ??
      this.#mediaGroup()?.querySelector(".product-card__media-pagination--below[data-media-pagination]") ??
      null
    );
  }

  #isCarouselProductLinkTarget(event) {
    const target = event.target;
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest(".product-card__media-slide a[href]"));
  }

  #stopCarouselEventPropagation = (event) => {
    event.stopPropagation();
  };

  #onTrackPointerCapture = (event) => {
    if (this.#isCarouselProductLinkTarget(event)) return;
    event.stopPropagation();
  };

  #bindCarouselChromeIsolation(signal) {
    const media = this.closest(".product-card__media--carousel");
    const chromeRoots = [
      ...(media ? media.querySelectorAll(".product-card__media-nav-host, .product-card__actions, .product-card__badges, product-badge.product-card__badges") : []),
      ...(this.#pagination ? [this.#pagination] : []),
    ];

    for (const root of chromeRoots) {
      for (const eventName of ["click", "pointerdown", "mousedown"]) {
        root.addEventListener(eventName, this.#stopCarouselEventPropagation, { signal });
      }
    }
  }

  connectedCallback() {
    this.#track = this.querySelector("[data-media-track]");
    this.#prev = this.querySelector("[data-media-prev]");
    this.#next = this.querySelector("[data-media-next]");
    this.#pagination = this.#resolvePagination();
    this.#dots = this.#pagination
      ? [...this.#pagination.querySelectorAll("[data-media-dot]")].filter(
          (node) => node instanceof HTMLButtonElement
        )
      : [];
    if (!this.#track) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#prev?.addEventListener("click", this.#onPrev, { signal });
    this.#next?.addEventListener("click", this.#onNext, { signal });
    this.#track.addEventListener("scroll", this.#sync, { passive: true, signal });
    for (const dot of this.#dots) {
      dot.addEventListener("click", this.#onDotClick, { signal });
    }

    for (const eventName of ["pointerdown", "touchstart", "mousedown"]) {
      this.#track.addEventListener(eventName, this.#onTrackPointerCapture, {
        capture: true,
        passive: eventName === "touchstart",
        signal,
      });
    }

    this.#bindCarouselChromeIsolation(signal);

    this.#resizeObserver = new ResizeObserver(this.#sync);
    this.#resizeObserver.observe(this.#track);
    this.#sync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#track = null;
    this.#prev = null;
    this.#next = null;
    this.#pagination = null;
    this.#dots = [];
  }

  #scrollAmount() {
    if (!this.#track) return 0;
    const item = this.#track.querySelector(".product-card__media-slide");
    if (!(item instanceof HTMLElement)) return this.#track.clientWidth;
    const styles = getComputedStyle(this.#track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  #onPrev = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#track?.scrollBy({ left: -this.#scrollAmount(), behavior: "smooth" });
  };

  #onNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#track?.scrollBy({ left: this.#scrollAmount(), behavior: "smooth" });
  };

  #onDotClick = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLButtonElement) || !this.#track) return;
    event.preventDefault();
    event.stopPropagation();
    const index = Number.parseInt(target.dataset.mediaDot ?? "", 10);
    if (!Number.isFinite(index)) return;
    const amount = this.#scrollAmount();
    if (amount <= 0) return;
    this.#track.scrollTo({ left: index * amount, behavior: "smooth" });
  };

  #activeSlideIndex() {
    if (!this.#track) return 0;
    const amount = this.#scrollAmount();
    if (amount <= 0) return 0;
    const maxIndex = Math.max(0, this.#dots.length - 1);
    return Math.min(maxIndex, Math.max(0, Math.round(this.#track.scrollLeft / amount)));
  }

  #syncDots = () => {
    if (this.#dots.length === 0) return;
    const activeIndex = this.#activeSlideIndex();
    for (const [index, dot] of this.#dots.entries()) {
      const isActive = index === activeIndex;
      dot.classList.toggle("product-card__media-pagination-bullet--active", isActive);
      dot.classList.toggle("swiper-pagination-bullet-active", isActive);
      dot.setAttribute("aria-selected", isActive ? "true" : "false");
    }
  };

  #sync = () => {
    if (!this.#track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    const maxScroll = scrollWidth - clientWidth;
    const hasOverflow = maxScroll > 1;
    this.classList.toggle("product-card__media-carousel--overflow", hasOverflow);
    this.#mediaGroup()?.classList.toggle("product-card__media-group--overflow", hasOverflow);
    if (this.#prev) this.#prev.disabled = !hasOverflow || scrollLeft <= 1;
    if (this.#next) this.#next.disabled = !hasOverflow || scrollLeft >= maxScroll - 1;
    this.#syncDots();
  };
}

if (!customElements.get("product-card-media-carousel")) {
  customElements.define("product-card-media-carousel", ProductCardMediaCarousel);
}
